import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import {
  Film, Plus, Trash2, Pencil, Copy, Upload, Globe, MapPin, Monitor, Package, Users,
  Star, ToggleLeft, ToggleRight,
  Wifi, CheckCircle2, AlertCircle, RefreshCw, Shuffle, Search,
} from 'lucide-react';
import type {
  Video, VideoPlaylist, PlaylistAssignment, VideoPeer,
  Targeting,
} from '@/types/demo';
import { PRODUCT_FAMILIES } from '@/types/demo';
import { getTargetedStationIds } from '@/lib/targeting';
import type { DemoLocation, DemoStation } from '@/types/demo';
import { sendCommand } from '@/lib/api/demoStations';
import { toast } from 'sonner';
import { r2Upload, r2PresignPut } from '@/lib/r2';


// ── helpers ───────────────────────────────────────────────────────────────────

function fmtBytes(b: number | null): string {
  if (!b) return '—';
  if (b >= 1e9) return `${(b / 1e9).toFixed(1)} GB`;
  if (b >= 1e6) return `${(b / 1e6).toFixed(0)} MB`;
  return `${(b / 1e3).toFixed(0)} KB`;
}


type UploadStep = 'queued' | 'uploading' | 'done' | 'error';

interface UploadQueueItem {
  id: string;
  file: File;
  name: string;
  step: UploadStep;
  pct: number;
  msg: string;
}

interface PlaylistFormItem { _key: string; video_id: string; }
interface PlaylistForm     { name: string; items: PlaylistFormItem[]; is_flagship: boolean; flagship_interval_mins: number; }
interface AssignmentForm   { playlist_id: string; targeting: Targeting; priority: number; is_active: boolean; }

function extractThumbnail(file: File): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;
    const url = URL.createObjectURL(file);
    video.src = url;
    video.onloadeddata = () => { video.currentTime = Math.min(0.5, video.duration); };
    video.onseeked = () => {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext('2d')!.drawImage(video, 0, 0);
      canvas.toBlob(
        blob => { URL.revokeObjectURL(url); blob ? resolve(blob) : reject(new Error('Thumbnail capture failed')); },
        'image/jpeg', 0.75,
      );
    };
    video.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Failed to load video for thumbnail')); };
  });
}

const BLANK_PLAYLIST:   PlaylistForm   = { name: '', items: [], is_flagship: false, flagship_interval_mins: 5 };
const BLANK_ASSIGNMENT: AssignmentForm = { playlist_id: '', targeting: {}, priority: 0, is_active: true };

// ── Component ─────────────────────────────────────────────────────────────────

export function VideoPlaylistsPage() {
  const qc  = useQueryClient();
  const [tab, setTab] = useState<'videos' | 'playlists' | 'assignments'>(() => {
    const saved = localStorage.getItem('videoPlaylistsTab');
    return saved === 'videos' || saved === 'playlists' || saved === 'assignments' ? saved : 'videos';
  });

  // ── Queries ────────────────────────────────────────────────────────────────

  const { data: videos = [], isLoading: vLoading } = useQuery<Video[]>({
    queryKey: ['videos'],
    queryFn: async () => { const { data, error } = await supabase.from('videos').select('*').order('created_at', { ascending: false }); if (error) throw error; return data ?? []; },
    refetchInterval: 30_000,
  });

  const { data: playlists = [], isLoading: pLoading } = useQuery<(VideoPlaylist & { item_count: number; is_flagship: boolean; flagship_interval_mins: number })[]>({
    queryKey: ['video-playlists'],
    queryFn: async () => {
      const { data, error } = await supabase.from('video_playlists').select('*, items:video_playlist_items(id)').order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []).map((p: any) => ({ ...p, item_count: p.items?.length ?? 0, is_flagship: p.is_flagship ?? false }));
    },
  });

  const { data: assignments = [], isLoading: aLoading } = useQuery<(PlaylistAssignment & { peer_summary: VideoPeer[] })[]>({
    queryKey: ['playlist-assignments'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('playlist_assignments')
        .select('*, playlist:video_playlists(id, name)')
        .order('priority', { ascending: false })
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
    refetchInterval: 30_000,
  });

  const { data: peers = [] } = useQuery<VideoPeer[]>({
    queryKey: ['video-peers'],
    queryFn: async () => { const { data } = await supabase.from('video_peers').select('*'); return data ?? []; },
    refetchInterval: 30_000,
  });

  const { data: locations = [] } = useQuery<DemoLocation[]>({
    queryKey: ['demo-locations'],
    queryFn: async () => { const { data } = await supabase.from('demo_locations').select('*').order('sort_order'); return data ?? []; },
  });

  const { data: stations = [] } = useQuery<(DemoStation & { location: { name: string } | null })[]>({
    queryKey: ['demo-stations-list'],
    queryFn: async () => { const { data } = await supabase.from('demo_stations').select('id, name, location_id, build_id, status, network_bandwidth_mbps, location:demo_locations(name)').order('name'); return (data ?? []) as unknown as (DemoStation & { location: { name: string } | null })[]; },
  });

  const { data: builds = [] } = useQuery<{ id: string; sku: string; description: string | null }[]>({
    queryKey: ['builds-simple'],
    queryFn: async () => { const { data } = await supabase.from('builds').select('id, sku, description').order('sku'); return data ?? []; },
  });

  // ── Video search ───────────────────────────────────────────────────────────

  const [videoSearch, setVideoSearch] = useState('');
  const filteredVideos = videoSearch.trim()
    ? videos.filter(v => v.name.toLowerCase().includes(videoSearch.trim().toLowerCase()))
    : videos;

  // ── Propagation panel ─────────────────────────────────────────────────────

  const [expandedVideoId, setExpandedVideoId] = useState<string | null>(null);

  function getVideoPeers(videoId: string) {
    return peers.filter(p => p.video_id === videoId);
  }

  function peerStatusConfig(status: string, tunnelUrl?: string | null): { label: string; dot: string; bar: string } {
    switch (status) {
      case 'seeding':         return tunnelUrl
        ? { label: 'Seeding',  dot: 'bg-green-500',  bar: 'bg-green-500' }
        : { label: 'Cached',   dot: 'bg-gray-400',   bar: 'bg-gray-400' };
      case 'downloading':     return { label: 'Downloading',    dot: 'bg-blue-500',   bar: 'bg-blue-500' };
      case 'ready_for_upload':return { label: 'Ready',          dot: 'bg-yellow-500', bar: 'bg-yellow-500' };
      case 'error':           return { label: 'Error',          dot: 'bg-red-500',    bar: 'bg-red-500' };
      default:                return { label: 'Pending',        dot: 'bg-gray-400',   bar: 'bg-gray-400' };
    }
  }

  // ── Upload queue ────────────────────────────────────────────────────────────

  const fileRef        = useRef<HTMLInputElement>(null);
  const [uploadQueue, setUploadQueue] = useState<UploadQueueItem[]>([]);
  const busyStationsRef = useRef<Set<string>>(new Set()); // station IDs currently uploading to
  const dispatchingRef  = useRef(false); // prevents concurrent processQueue runs

  function updateQueueItem(id: string, patch: Partial<UploadQueueItem>) {
    setUploadQueue(q => q.map(item => item.id === id ? { ...item, ...patch } : item));
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const newItems: UploadQueueItem[] = Array.from(files).map(f => ({
      id:   crypto.randomUUID(),
      file: f,
      name: f.name.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' '),
      step: 'queued' as const,
      pct:  0,
      msg:  'Queued',
    }));
    setUploadQueue(q => [...q, ...newItems]);
    if (fileRef.current) fileRef.current.value = '';
    setTimeout(() => processQueue(), 0);
  }

  function removeFromQueue(id: string) {
    setUploadQueue(q => q.filter(item => item.id !== id));
  }

  function clearFinished() {
    setUploadQueue(q => q.filter(item => item.step !== 'done' && item.step !== 'error'));
  }

  async function processQueue() {
    if (dispatchingRef.current) return;
    dispatchingRef.current = true;
    try {
      // Snapshot the queue
      await new Promise(r => setTimeout(r, 0));
      const snapshot = await new Promise<UploadQueueItem[]>(r => setUploadQueue(q => { r(q); return q; }));
      const queued = snapshot.filter(item => item.step === 'queued');
      if (queued.length === 0) return;

      // Find online stations with active tunnels
      const { data: stationsData } = await supabase
        .from('demo_stations')
        .select('id, name, last_seen, status')
        .neq('status', 'retiring');
      const cutoff    = Date.now() - 90_000;
      const onlineIds = (stationsData ?? [])
        .filter((s: any) => s.last_seen && new Date(s.last_seen).getTime() > cutoff)
        .map((s: any) => s.id as string);

      // Get unique stations with tunnels, excluding ones already busy
      const tunnelCutoff = new Date(Date.now() - 10 * 60 * 1000).toISOString();
      const { data: tunnelPeers } = await supabase
        .from('video_peers')
        .select('station_id, tunnel_url')
        .eq('status', 'seeding')
        .not('tunnel_url', 'is', null)
        .in('station_id', onlineIds.length > 0 ? onlineIds : ['__none__'])
        .gte('updated_at', tunnelCutoff);

      const stationTunnels = new Map<string, { tunnelUrl: string; name: string }>();
      for (const p of tunnelPeers ?? []) {
        if (!busyStationsRef.current.has(p.station_id) && !stationTunnels.has(p.station_id)) {
          const st = (stationsData ?? []).find((s: any) => s.id === p.station_id);
          stationTunnels.set(p.station_id, { tunnelUrl: p.tunnel_url, name: (st as any)?.name ?? 'Station' });
        }
      }

      const freeStations = [...stationTunnels.entries()];
      const dispatched   = Math.min(freeStations.length, queued.length);

      // Dispatch P2P uploads in parallel — one per available station
      for (let i = 0; i < dispatched; i++) {
        const [stationId, { tunnelUrl, name: stationName }] = freeStations[i];
        const item = queued[i];
        busyStationsRef.current.add(stationId);
        uploadOneVideo(item, tunnelUrl, stationName).finally(() => {
          busyStationsRef.current.delete(stationId);
          processQueue(); // try to fill freed slot
        });
      }

      // If items remain and no stations available, cloud-upload one at a time
      const remaining = queued.slice(dispatched);
      if (remaining.length > 0 && freeStations.length === 0 && busyStationsRef.current.size === 0) {
        uploadOneVideo(remaining[0], null, null).finally(() => processQueue());
      }
    } finally {
      dispatchingRef.current = false;
    }
  }

  async function uploadOneVideo(item: UploadQueueItem, tunnelUrl: string | null, stationName: string | null) {
    const { id, file, name } = item;
    const update = (patch: Partial<UploadQueueItem>) => updateQueueItem(id, patch);

    update({ step: 'uploading', msg: 'Creating video record…', pct: 0 });

    try {
      const videoId = id;

      // 1. Create video record (idempotent — the P2P→cloud fallback re-invokes
      //    this function with the same id, so a plain insert would collide on
      //    videos_pkey for the row the first attempt already created).
      const { error: vErr } = await supabase.from('videos').upsert({
        id:        videoId,
        name:      name.trim(),
        file_name: file.name,
        file_size: file.size,
      });
      if (vErr) throw vErr;

      // 1b. Generate & upload thumbnail in the background
      extractThumbnail(file).then(async (blob) => {
        try {
          const thumbKey = `demo-assets/video-thumbnails/${videoId}.jpg`;
          const publicUrl = await r2Upload(thumbKey, blob, 'image/jpeg');
          await supabase.from('videos').update({ thumbnail_url: publicUrl }).eq('id', videoId);
          qc.invalidateQueries({ queryKey: ['videos'] });
        } catch (e) { console.warn('[VideoUpload] Thumbnail failed:', e); }
      }).catch(e => console.warn('[VideoUpload] Thumbnail extraction failed:', e));

      // 2. Upload via P2P if tunnel provided
      if (tunnelUrl && stationName) {
        update({ msg: `Uploading to ${stationName}…` });
        const CHUNK = 5 * 1024 * 1024;
        const total = file.size;
        let offset  = 0;

        while (offset < total) {
          const end = Math.min(offset + CHUNK, total);
          const buf = await file.slice(offset, end).arrayBuffer();
          const url = `${tunnelUrl}/upload/${videoId}`
            + `?name=${encodeURIComponent(file.name)}&offset=${offset}&total=${total}`;

          const resp = await fetch(url, {
            method:  'POST',
            headers: { 'Content-Type': 'text/plain' },
            body:    buf,
          });

          if (!resp.ok) throw new Error(`Chunk upload failed: HTTP ${resp.status}`);
          offset = end;
          update({ pct: Math.round(offset / total * 100) });
        }

        update({ step: 'done', pct: 100, msg: `Uploaded to ${stationName}` });
        qc.invalidateQueries({ queryKey: ['videos'] });
        toast.success(`${name} uploaded to ${stationName}`);
        return;
      }

      // 3. Cloud fallback
      // Store the raw filename as the object key - percent-encoding here would
      // make "%20" part of the key itself, and the public URL would 404.
      // Encoding happens when the URL is built, not when the key is.
      const r2Key = `videos/${videoId}/${file.name}`;
      update({ msg: 'Preparing cloud upload…' });
      const { url: presignedUrl, publicUrl: r2PublicUrl } = await r2PresignPut(
        r2Key,
        file.type || 'video/mp4',
      );

      update({ msg: 'Uploading to cloud…' });
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', presignedUrl);
        xhr.setRequestHeader('Content-Type', file.type || 'video/mp4');
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) update({ pct: Math.round(e.loaded / e.total * 100) });
        };
        xhr.onload  = () => xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed: HTTP ${xhr.status}`));
        xhr.onerror = () => reject(new Error('Network error during upload'));
        xhr.send(file);
      });

      await supabase.from('videos').update({ storage_url: r2PublicUrl }).eq('id', videoId);

      update({ step: 'done', pct: 100, msg: 'Uploaded to cloud' });
      qc.invalidateQueries({ queryKey: ['videos'] });
      toast.success(`${name} uploaded`);
    } catch (err: any) {
      // If P2P failed, fall back to cloud for this item
      if (tunnelUrl) {
        console.error('[VideoUpload] Station upload failed:', err);
        update({ pct: 0, msg: `${stationName} failed — falling back to cloud…` });
        await new Promise(r => setTimeout(r, 1_000));
        return uploadOneVideo({ ...item, step: 'uploading' as const }, null, null);
      }
      update({ step: 'error', msg: err.message });
      toast.error(`${name}: ${err.message}`);
    }
  }

  const deleteVideo = useMutation({
    mutationFn: async (v: Video) => {
      // Remove from all playlists and clean up peer records first
      await supabase.from('video_playlist_items').delete().eq('video_id', v.id);
      await supabase.from('video_peers').delete().eq('video_id', v.id);
      const { error } = await supabase.from('videos').delete().eq('id', v.id);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['videos'] }); qc.invalidateQueries({ queryKey: ['video-playlists'] }); toast.success('Video deleted'); },
    onError: (e: Error) => toast.error(e.message),
  });

  // ── Playlist mutations ─────────────────────────────────────────────────────

  const [plDialogOpen,  setPlDialogOpen]  = useState(false);
  const [editingPlId,   setEditingPlId]   = useState<string | null>(null);
  const [plForm,        setPlForm]        = useState<PlaylistForm>(BLANK_PLAYLIST);
  const [plVideoSearch, setPlVideoSearch] = useState('');

  function openCreatePlaylist() { setEditingPlId(null); setPlForm(BLANK_PLAYLIST); setPlVideoSearch(''); setPlDialogOpen(true); }

  async function openEditPlaylist(pl: VideoPlaylist & { is_flagship?: boolean }) {
    setEditingPlId(pl.id);
    setPlVideoSearch('');
    const { data } = await supabase.from('video_playlist_items').select('*, video:videos(*)').eq('playlist_id', pl.id).order('sort_order');
    setPlForm({
      name: pl.name,
      is_flagship: pl.is_flagship ?? false,
      flagship_interval_mins: (pl as any).flagship_interval_mins ?? 5,
      items: (data ?? []).map((i: any) => ({ _key: i.id, video_id: i.video_id })),
    });
    setPlDialogOpen(true);
  }

  // Load an existing playlist's videos into a new (unsaved) playlist. Flagship
  // is not carried over — there can only be one, and saving a copy would steal it.
  async function duplicatePlaylist(sourceId: string) {
    const src = playlists.find(p => p.id === sourceId);
    if (!src) return;
    const { data, error } = await supabase.from('video_playlist_items').select('id, video_id').eq('playlist_id', sourceId).order('sort_order');
    if (error) { toast.error(error.message); return; }
    setEditingPlId(null);
    setPlVideoSearch('');
    setPlForm({
      name: `${src.name} (copy)`,
      is_flagship: false,
      flagship_interval_mins: (src as any).flagship_interval_mins ?? 5,
      items: (data ?? []).map((i: any) => ({ _key: `${Date.now()}-${i.id}`, video_id: i.video_id })),
    });
    setPlDialogOpen(true);
  }

  const upsertPlaylist = useMutation({
    mutationFn: async ({ id, form }: { id?: string; form: PlaylistForm }) => {
      // If marking as flagship, clear any other flagship playlist first
      if (form.is_flagship) {
        await supabase.from('video_playlists').update({ is_flagship: false }).eq('is_flagship', true);
      }
      let plId = id;
      const payload = { name: form.name.trim(), is_flagship: form.is_flagship, flagship_interval_mins: form.flagship_interval_mins };
      if (id) {
        const { error } = await supabase.from('video_playlists').update(payload).eq('id', id);
        if (error) throw error;
      } else {
        const { data, error } = await supabase.from('video_playlists').insert(payload).select('id').single();
        if (error) throw error;
        plId = data.id;
      }
      await supabase.from('video_playlist_items').delete().eq('playlist_id', plId!);
      if (form.items.length > 0) {
        const { error } = await supabase.from('video_playlist_items').insert(
          form.items.map((item, i) => ({ playlist_id: plId, video_id: item.video_id, sort_order: i }))
        );
        if (error) throw error;
      }
      // Notify stations so they pick up the flagship change
      if (form.is_flagship) {
        const { data: stList } = await supabase.from('demo_stations').select('id').neq('status', 'retiring');
        await Promise.allSettled((stList ?? []).map((s: { id: string }) => sendCommand(s.id, 'sync_playlist')));
      }
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['video-playlists'] }); setPlDialogOpen(false); toast.success(editingPlId ? 'Playlist updated' : 'Playlist created'); },
    onError: (e: Error) => toast.error(e.message),
  });

  const deletePlaylist = useMutation({
    mutationFn: async (id: string) => { const { error } = await supabase.from('video_playlists').delete().eq('id', id); if (error) throw error; },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['video-playlists'] }); toast.success('Playlist deleted'); },
    onError: (e: Error) => toast.error(e.message),
  });



  // ── Assignment mutations ───────────────────────────────────────────────────

  const [asgDialogOpen, setAsgDialogOpen] = useState(false);
  const [editingAsgId,  setEditingAsgId]  = useState<string | null>(null);
  const [asgForm,       setAsgForm]       = useState<AssignmentForm>(BLANK_ASSIGNMENT);

  function openCreateAssignment() { setEditingAsgId(null); setAsgForm(BLANK_ASSIGNMENT); setAsgDialogOpen(true); }
  function openEditAssignment(a: PlaylistAssignment) {
    setEditingAsgId(a.id);
    setAsgForm({ playlist_id: a.playlist_id, targeting: a.targeting ?? {}, priority: a.priority, is_active: a.is_active });
    setAsgDialogOpen(true);
  }

  const upsertAssignment = useMutation({
    mutationFn: async ({ id, form }: { id?: string; form: AssignmentForm }) => {
      const now = new Date().toISOString();
      const payload: Record<string, unknown> = {
        playlist_id: form.playlist_id,
        targeting:   form.targeting,
        target_type: 'all',  // legacy column — kept for backwards compat
        priority:    form.priority,
        is_active:   form.is_active,
        // Set started_at when activating so wall-clock sync kicks in from now
        ...(form.is_active ? { started_at: now } : {}),
      };
      if (id) { const { error } = await supabase.from('playlist_assignments').update(payload).eq('id', id); if (error) throw error; }
      else     { const { error } = await supabase.from('playlist_assignments').insert(payload); if (error) throw error; }
      const { data: stList } = await supabase.from('demo_stations').select('id').neq('status', 'retiring');
      await Promise.allSettled((stList ?? []).map((s: { id: string }) => sendCommand(s.id, 'sync_playlist')));
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['playlist-assignments'] }); setAsgDialogOpen(false); toast.success(editingAsgId ? 'Assignment updated' : 'Assignment created'); },
    onError: (e: Error) => toast.error(e.message),
  });

  const toggleAssignment = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const update: Record<string, unknown> = { is_active };
      // Reset started_at when re-activating so all stations resync from position 0
      if (is_active) update.started_at = new Date().toISOString();
      const { error } = await supabase.from('playlist_assignments').update(update).eq('id', id);
      if (error) throw error;
      const { data: stList } = await supabase.from('demo_stations').select('id').neq('status', 'retiring');
      await Promise.allSettled((stList ?? []).map((s: { id: string }) => sendCommand(s.id, 'sync_playlist')));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['playlist-assignments'] }),
    onError: (e: Error) => toast.error(e.message),
  });

  const resyncAssignment = useMutation({
    mutationFn: async (id: string) => {
      // Reset started_at to now — all stations will jump back to position 0 in sync
      const { error } = await supabase.from('playlist_assignments').update({ started_at: new Date().toISOString() }).eq('id', id);
      if (error) throw error;
      const { data: stList } = await supabase.from('demo_stations').select('id').neq('status', 'retiring');
      await Promise.allSettled((stList ?? []).map((s: { id: string }) => sendCommand(s.id, 'sync_playlist')));
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['playlist-assignments'] }); toast.success('Playlist resynced from the beginning'); },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteAssignment = useMutation({
    mutationFn: async (id: string) => { const { error } = await supabase.from('playlist_assignments').delete().eq('id', id); if (error) throw error; },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['playlist-assignments'] }); toast.success('Assignment deleted'); },
    onError: (e: Error) => toast.error(e.message),
  });

  // ── Per-video peer stats ───────────────────────────────────────────────────

  // ── Target label ───────────────────────────────────────────────────────────

  function targetLabel(a: PlaylistAssignment): string {
    const t = a.targeting ?? {};
    const parts: string[] = [];
    if (t.locations?.length) parts.push(t.locations.map(id => locations.find(l => l.id === id)?.name ?? '?').join(', '));
    if (t.families?.length) parts.push(t.families.map(c => PRODUCT_FAMILIES[c] ?? c).join(', '));
    if (t.builds?.length) parts.push(t.builds.map(id => builds.find(b => b.id === id)?.sku ?? '?').join(', '));
    if (t.stations?.length) parts.push(t.stations.map(id => stations.find(s => s.id === id)?.name ?? '?').join(', '));
    return parts.length > 0 ? parts.join(' + ') : 'All stations';
  }

  const canSavePlaylist   = plForm.name.trim() && plForm.items.length > 0;
  const canSaveAssignment = !!asgForm.playlist_id;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="max-w-6xl mx-auto space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Film className="h-6 w-6 text-primary" />
          <div>
            <h1 className="text-xl font-semibold">Video Playlists</h1>
            <p className="text-sm text-muted-foreground">P2P video distribution — upload once, stations share and seed automatically</p>
          </div>
        </div>
        {tab === 'playlists' && <Button onClick={openCreatePlaylist} size="sm" className="gap-2"><Plus className="h-4 w-4" /> New Playlist</Button>}
        {tab === 'assignments' && <Button onClick={openCreateAssignment} size="sm" className="gap-2"><Plus className="h-4 w-4" /> New Assignment</Button>}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b">
        {(['videos', 'playlists', 'assignments'] as const).map(t => (
          <button key={t} onClick={() => { setTab(t); localStorage.setItem('videoPlaylistsTab', t); }} className={cn('px-4 py-2 text-sm font-medium capitalize transition-colors border-b-2 -mb-px', tab === t ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground')}>
            {t === 'videos' ? `Videos (${videos.length})` : t === 'playlists' ? `Playlists (${playlists.length})` : `Assignments (${assignments.length})`}
          </button>
        ))}
      </div>

      {/* ── Videos tab ─────────────────────────────────────────────────────── */}
      {tab === 'videos' && (
        <div className="space-y-6">
          {/* Upload card */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-sm font-semibold flex items-center gap-2"><Upload className="h-4 w-4" /> Upload Videos</h2>
                  <p className="text-xs text-muted-foreground">Select one or more files — they queue and upload one at a time via P2P or cloud.</p>
                </div>
                {uploadQueue.some(i => i.step === 'done' || i.step === 'error') && (
                  <Button size="sm" variant="ghost" onClick={clearFinished} className="text-xs text-muted-foreground h-7">
                    Clear finished
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* P2P readiness indicator */}
              {(() => {
                const freshCutoff = Date.now() - 10 * 60 * 1000; // 10 min — tunnel refresh is every 5 min
                const tunnelStationIds = new Set(
                  peers.filter(p => p.status === 'seeding' && p.tunnel_url && new Date(p.updated_at).getTime() > freshCutoff).map(p => p.station_id)
                );
                const readyStations = stations.filter(s => tunnelStationIds.has(s.id) && s.status !== 'retiring');
                return (
                  <div className={cn('flex items-center gap-2 text-xs px-2.5 py-1.5 rounded-md',
                    readyStations.length > 0
                      ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400'
                      : 'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400'
                  )}>
                    <span className={cn('h-2 w-2 rounded-full flex-shrink-0', readyStations.length > 0 ? 'bg-green-500' : 'bg-yellow-500')} />
                    {readyStations.length > 0 ? (
                      <span>P2P ready — {readyStations.map(s => s.name).join(', ')}</span>
                    ) : (
                      <span>No station tunnels active — uploads will go to cloud</span>
                    )}
                  </div>
                );
              })()}
              <div>
                <input ref={fileRef} type="file" accept="video/mp4,video/webm" multiple onChange={handleFileChange}
                  className="block w-full text-sm text-muted-foreground file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-xs file:font-medium file:bg-primary file:text-primary-foreground hover:file:opacity-90 cursor-pointer" />
              </div>

              {/* Upload queue */}
              {uploadQueue.length > 0 && (
                <div className="space-y-2">
                  {uploadQueue.map(item => (
                    <div key={item.id} className={cn('rounded-md px-3 py-2 text-sm',
                      item.step === 'error'     ? 'bg-red-50 dark:bg-red-900/20' :
                      item.step === 'done'      ? 'bg-green-50 dark:bg-green-900/20' :
                      item.step === 'uploading' ? 'bg-blue-50 dark:bg-blue-900/20' :
                                                  'bg-gray-50 dark:bg-gray-800/50'
                    )}>
                      <div className="flex items-center gap-2">
                        {item.step === 'error'     && <AlertCircle className="h-3.5 w-3.5 text-red-500 flex-shrink-0" />}
                        {item.step === 'done'      && <CheckCircle2 className="h-3.5 w-3.5 text-green-600 flex-shrink-0" />}
                        {item.step === 'uploading' && <div className="h-3.5 w-3.5 rounded-full border-2 border-blue-300 border-t-blue-600 animate-spin flex-shrink-0" />}
                        {item.step === 'queued'    && <Upload className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />}
                        <div className="flex-1 min-w-0">
                          {item.step === 'queued' ? (
                            <input
                              value={item.name}
                              onChange={e => updateQueueItem(item.id, { name: e.target.value })}
                              className="w-full bg-transparent text-sm font-medium outline-none border-b border-dashed border-gray-300 dark:border-gray-600 focus:border-primary"
                              placeholder="Display name"
                            />
                          ) : (
                            <span className="text-sm font-medium truncate block">{item.name}</span>
                          )}
                        </div>
                        <span className="text-[11px] text-muted-foreground flex-shrink-0">{fmtBytes(item.file.size)}</span>
                        {item.step === 'uploading' && (
                          <span className="text-xs font-medium text-blue-600 flex-shrink-0 w-8 text-right">{item.pct}%</span>
                        )}
                        {item.step === 'queued' && (
                          <button onClick={() => removeFromQueue(item.id)} className="p-0.5 rounded hover:bg-red-100 dark:hover:bg-red-900/30 flex-shrink-0">
                            <Trash2 className="h-3.5 w-3.5 text-red-400" />
                          </button>
                        )}
                      </div>
                      {item.step === 'uploading' && (
                        <div className="mt-1.5">
                          <div className="flex items-center justify-between mb-0.5">
                            <span className="text-[11px] text-blue-600 dark:text-blue-400 truncate">{item.msg}</span>
                          </div>
                          <div className="h-1.5 rounded-full bg-blue-200 dark:bg-blue-800">
                            <div className="h-1.5 rounded-full bg-blue-600 transition-all" style={{ width: `${item.pct}%` }} />
                          </div>
                        </div>
                      )}
                      {item.step === 'error' && (
                        <p className="text-[11px] text-red-500 mt-0.5 truncate">{item.msg}</p>
                      )}
                      {item.step === 'done' && (
                        <p className="text-[11px] text-green-600 dark:text-green-400 mt-0.5">{item.msg}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Search + Video grid */}
          {!vLoading && videos.length > 0 && (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search videos..."
                value={videoSearch}
                onChange={e => setVideoSearch(e.target.value)}
                className="w-full h-9 pl-9 pr-3 rounded-md border border-input bg-background text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          )}
          {vLoading && <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">{[1,2,3,4].map(i => <div key={i} className="aspect-video rounded-lg bg-gray-100 dark:bg-gray-800 animate-pulse" />)}</div>}
          {!vLoading && videos.length === 0 && (
            <div className="flex flex-col items-center py-12 text-muted-foreground gap-2"><Film className="h-8 w-8 opacity-30" /><p className="text-sm">No videos yet</p></div>
          )}
          {!vLoading && videos.length > 0 && filteredVideos.length === 0 && (
            <div className="flex flex-col items-center py-8 text-muted-foreground gap-1"><Search className="h-6 w-6 opacity-30" /><p className="text-sm">No videos matching "{videoSearch.trim()}"</p></div>
          )}
          {filteredVideos.length > 0 && (
            <div className="flex gap-4">
              <div className="flex-1 min-w-0 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 auto-rows-min content-start">
                {filteredVideos.map(v => {
                  const vPeers  = getVideoPeers(v.id);
                  const seeders = vPeers.filter(p => p.status === 'seeding').length;
                  const active  = vPeers.filter(p => p.status === 'downloading').length;
                  const isExpanded = expandedVideoId === v.id;
                  return (
                    <div
                      key={v.id}
                      onClick={() => setExpandedVideoId(isExpanded ? null : v.id)}
                      className={cn('group rounded-lg border bg-gray-50 dark:bg-gray-900 overflow-hidden cursor-pointer transition-all', isExpanded && 'ring-2 ring-primary border-primary')}
                    >
                      <div className="aspect-video bg-gray-900 flex items-center justify-center relative">
                        {v.thumbnail_url
                          ? <img src={v.thumbnail_url} alt={v.name} className="w-full h-full object-cover" />
                          : <Film className="h-8 w-8 text-gray-600" />}
                        <span className="absolute bottom-1 right-1 bg-black/70 text-white text-[9px] px-1.5 py-0.5 rounded">
                          {fmtBytes(v.file_size)}
                        </span>
                        {active > 0 && (
                          <span className="absolute top-1 left-1 bg-blue-600/90 text-white text-[9px] px-1.5 py-0.5 rounded flex items-center gap-1">
                            <span className="inline-block h-1.5 w-1.5 rounded-full bg-white animate-pulse" />
                            {active} dl
                          </span>
                        )}
                      </div>
                      <div className="p-2 space-y-1.5">
                        <p className="text-xs font-medium truncate">{v.name}</p>
                        <div className="flex items-center justify-between">
                          <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
                            <Wifi className="h-3 w-3" /> {seeders} seed{seeders !== 1 ? 's' : ''}
                          </span>
                          <button onClick={e => { e.stopPropagation(); if (confirm(`Delete "${v.name}"?`)) deleteVideo.mutate(v); }}
                            className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30">
                            <Trash2 className="h-3.5 w-3.5 text-red-500" />
                          </button>
                        </div>
                        {/* Status dots — one per peer */}
                        {vPeers.length > 0 && (
                          <div className="flex gap-0.5 flex-wrap">
                            {vPeers.map(p => {
                              const cfg = peerStatusConfig(p.status);
                              const st  = stations.find(s => s.id === p.station_id);
                              return <div key={p.station_id} title={`${st?.name ?? 'Station'}: ${cfg.label}${p.status === 'downloading' ? ` ${p.progress_pct}%` : ''}`} className={cn('h-1.5 w-1.5 rounded-full', cfg.dot)} />;
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Right-side preview pane */}
              {expandedVideoId && (() => {
                const v      = videos.find(x => x.id === expandedVideoId);
                const vPeers = getVideoPeers(expandedVideoId);
                if (!v) return null;
                return (
                  <div className="w-72 flex-shrink-0 border rounded-lg bg-white dark:bg-gray-900 overflow-hidden self-start sticky top-4">
                    {/* Thumbnail / preview */}
                    <div className="aspect-video bg-gray-900 flex items-center justify-center">
                      {v.thumbnail_url
                        ? <img src={v.thumbnail_url} alt={v.name} className="w-full h-full object-cover" />
                        : <Film className="h-10 w-10 text-gray-600" />}
                    </div>
                    <div className="p-3 space-y-3">
                      <div>
                        <h3 className="text-sm font-semibold truncate">{v.name}</h3>
                        <p className="text-[11px] text-muted-foreground">{v.file_name} · {fmtBytes(v.file_size)}</p>
                      </div>
                      {/* Propagation */}
                      <div className="space-y-1.5">
                        <h4 className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                          <Wifi className="h-3 w-3" /> Propagation
                        </h4>
                        {vPeers.length === 0 ? (
                          <p className="text-xs text-muted-foreground">No stations downloading yet.</p>
                        ) : (
                          <div className="space-y-1.5">
                            {vPeers.map(p => {
                              const cfg     = peerStatusConfig(p.status, p.tunnel_url);
                              const station = stations.find(s => s.id === p.station_id);
                              return (
                                <div key={p.station_id} className="space-y-0.5">
                                  <div className="flex items-center gap-2">
                                    <div className={cn('h-1.5 w-1.5 rounded-full flex-shrink-0', p.status === 'downloading' ? `${cfg.dot} animate-pulse` : cfg.dot)} />
                                    <span className="text-xs font-medium truncate flex-1">{station?.name ?? 'Unknown'}</span>
                                    <span className={cn('text-[10px] flex-shrink-0', p.status === 'seeding' && p.tunnel_url ? 'text-green-600' : p.status === 'error' ? 'text-red-500' : 'text-muted-foreground')}>
                                      {cfg.label}{p.status === 'downloading' ? ` ${p.progress_pct}%` : ''}
                                    </span>
                                  </div>
                                  {p.status === 'downloading' && (
                                    <div className="h-1 rounded-full bg-gray-200 dark:bg-gray-700 ml-3.5">
                                      <div className={cn('h-1 rounded-full transition-all', cfg.bar)} style={{ width: `${p.progress_pct}%` }} />
                                    </div>
                                  )}
                                  {p.status === 'error' && p.error_message && (
                                    <p className="text-[10px] text-red-500 truncate ml-3.5">{p.error_message}</p>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      <p className="text-[10px] text-muted-foreground">Refreshes every 10 s</p>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {/* ── Playlists tab ───────────────────────────────────────────────────── */}
      {tab === 'playlists' && (
        <div className="space-y-3">
          {pLoading && [1,2,3].map(i => <div key={i} className="h-16 rounded-lg bg-gray-100 dark:bg-gray-800 animate-pulse" />)}
          {!pLoading && playlists.length === 0 && (
            <Card><CardContent className="flex flex-col items-center py-12 gap-2 text-muted-foreground"><Film className="h-8 w-8 opacity-30" /><p className="text-sm">No playlists yet</p><Button size="sm" variant="outline" onClick={openCreatePlaylist}>New Playlist</Button></CardContent></Card>
          )}
          {playlists.map(pl => (
            <Card key={pl.id}>
              <CardContent className="py-3 px-4 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-sm">{pl.name}</p>
                    {pl.is_flagship && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                        <Star className="h-2.5 w-2.5 fill-current" /> Flagship
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">{pl.item_count} video{pl.item_count !== 1 ? 's' : ''}</p>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => duplicatePlaylist(pl.id)} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800" title="Duplicate"><Copy className="h-4 w-4 text-gray-500" /></button>
                  <button onClick={() => openEditPlaylist(pl)} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800"><Pencil className="h-4 w-4 text-gray-500" /></button>
                  <button onClick={() => { if (confirm(`Delete "${pl.name}"?`)) deletePlaylist.mutate(pl.id); }} className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20"><Trash2 className="h-4 w-4 text-red-500" /></button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* ── Assignments tab ─────────────────────────────────────────────────── */}
      {tab === 'assignments' && (
        <div className="space-y-3">
          {aLoading && [1,2,3].map(i => <div key={i} className="h-16 rounded-lg bg-gray-100 dark:bg-gray-800 animate-pulse" />)}
          {!aLoading && assignments.length === 0 && (
            <Card><CardContent className="flex flex-col items-center py-12 gap-2 text-muted-foreground"><Film className="h-8 w-8 opacity-30" /><p className="text-sm">No assignments yet</p><Button size="sm" variant="outline" onClick={openCreateAssignment}>New Assignment</Button></CardContent></Card>
          )}
          {assignments.map(a => {
            const t = a.targeting ?? {};
            const hasTargeting = !!(t.locations?.length || t.families?.length || t.builds?.length || t.stations?.length);
            const TIcon = hasTargeting ? (t.stations?.length ? Monitor : t.locations?.length ? MapPin : t.families?.length ? Users : Package) : Globe;
            const pl = playlists.find(p => p.id === a.playlist_id);
            return (
              <Card key={a.id} className="overflow-hidden">
                <CardContent className="p-0">
                  <div className="flex items-center">
                    <div className="flex-1 px-4 py-3 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">{pl?.name ?? a.playlist_id}</span>
                        <span className={cn('px-2 py-0.5 rounded-full text-[11px] font-medium', a.is_active ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-500 dark:bg-gray-800')}>
                          {a.is_active ? 'Active' : 'Paused'}
                        </span>
                        <span className="px-2 py-0.5 rounded-full text-[11px] bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">P{a.priority}</span>
                      </div>
                      <div className="mt-1 flex items-center gap-3 text-[12px] text-muted-foreground">
                        <span className="flex items-center gap-1"><TIcon className="h-3 w-3" /> {targetLabel(a)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 pr-3">
                      {a.is_active && (
                        <button onClick={() => resyncAssignment.mutate(a.id)} className="p-1.5 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20" title="Resync all stations from the beginning">
                          <RefreshCw className={cn('h-4 w-4 text-blue-500', resyncAssignment.isPending && 'animate-spin')} />
                        </button>
                      )}
                      <button onClick={() => toggleAssignment.mutate({ id: a.id, is_active: !a.is_active })} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800" title={a.is_active ? 'Pause' : 'Activate'}>
                        {a.is_active ? <ToggleRight className="h-5 w-5 text-green-600" /> : <ToggleLeft className="h-5 w-5 text-gray-400" />}
                      </button>
                      <button onClick={() => openEditAssignment(a)} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800"><Pencil className="h-4 w-4 text-gray-500" /></button>
                      <button onClick={() => { if (confirm('Delete assignment?')) deleteAssignment.mutate(a.id); }} className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20"><Trash2 className="h-4 w-4 text-red-500" /></button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* ── Playlist dialog ─────────────────────────────────────────────────── */}
      <Dialog open={plDialogOpen} onOpenChange={setPlDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingPlId ? 'Edit Playlist' : 'New Playlist'}</DialogTitle></DialogHeader>
          <div className="space-y-5 py-2">
            {!editingPlId && playlists.length > 0 && (
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Start from</Label>
                <select value="" onChange={e => { if (e.target.value) duplicatePlaylist(e.target.value); }} className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm">
                  <option value="">Blank playlist — or duplicate an existing one…</option>
                  {playlists.map(p => <option key={p.id} value={p.id}>{p.name} ({p.item_count} video{p.item_count !== 1 ? 's' : ''})</option>)}
                </select>
              </div>
            )}
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Playlist name</Label>
              <Input placeholder="e.g. Currys Demo Loop" value={plForm.name} onChange={e => setPlForm(f => ({ ...f, name: e.target.value }))} />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-muted-foreground">Videos — tap to select</Label>
                <div className="flex items-center gap-2">
                  {plForm.items.length > 1 && (
                    <button type="button" onClick={() => setPlForm(f => {
                      const shuffled = [...f.items];
                      for (let i = shuffled.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]; }
                      return { ...f, items: shuffled };
                    })} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border bg-muted/50 hover:bg-muted text-muted-foreground hover:text-foreground transition-colors active:scale-95" title="Shuffle order">
                      <Shuffle className="h-3 w-3" /> Shuffle
                    </button>
                  )}
                  {plForm.items.length > 0 && <span className="text-xs text-muted-foreground">{plForm.items.length} selected</span>}
                </div>
              </div>
              <div className="space-y-2">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search videos..."
                    value={plVideoSearch}
                    onChange={e => setPlVideoSearch(e.target.value)}
                    className="w-full h-8 pl-8 pr-3 rounded-md border border-input bg-background text-xs placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2 max-h-[340px] overflow-y-auto rounded-md border p-2">
                  {(plVideoSearch.trim() ? videos.filter(v => v.name.toLowerCase().includes(plVideoSearch.trim().toLowerCase())) : videos).map(v => {
                    const playlistIdx = plForm.items.findIndex(item => item.video_id === v.id);
                    const inPlaylist = playlistIdx >= 0;
                    return (
                      <button key={v.id} type="button"
                        onClick={() => {
                          if (inPlaylist) {
                            setPlForm(f => ({ ...f, items: f.items.filter(item => item.video_id !== v.id) }));
                          } else {
                            setPlForm(f => ({ ...f, items: [...f.items, { _key: `${Date.now()}-${v.id}`, video_id: v.id }] }));
                          }
                        }}
                        className={cn(
                          'relative rounded-md overflow-hidden border-2 transition-all text-left',
                          inPlaylist ? 'border-primary ring-1 ring-primary/30' : 'border-transparent hover:border-gray-300 dark:hover:border-gray-600'
                        )}
                      >
                        <div className={cn('aspect-video bg-gray-100 dark:bg-gray-800 flex items-center justify-center transition-all', !inPlaylist && 'grayscale')}>
                          {v.thumbnail_url
                            ? <img src={v.thumbnail_url} alt="" className="w-full h-full object-cover" />
                            : <Film className="h-5 w-5 text-gray-400" />
                          }
                        </div>
                        <div className="px-1.5 py-1">
                          <p className="text-[11px] font-medium truncate">{v.name}</p>
                          <p className="text-[10px] text-muted-foreground">{fmtBytes(v.file_size)}</p>
                        </div>
                        {inPlaylist && (
                          <div className="absolute top-1 right-1 bg-primary text-primary-foreground rounded-full h-5 w-5 flex items-center justify-center text-[10px] font-bold">
                            {playlistIdx + 1}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
              {/* Flagship toggle */}
              <label className="flex items-center gap-2 pt-2 border-t cursor-pointer">
                <input type="checkbox" checked={plForm.is_flagship} onChange={e => setPlForm(f => ({ ...f, is_flagship: e.target.checked }))}
                  className="h-4 w-4 rounded text-amber-500" />
                <Star className={cn('h-4 w-4', plForm.is_flagship ? 'text-amber-500 fill-current' : 'text-gray-300')} />
                <div>
                  <span className="text-sm font-medium">Flagship playlist</span>
                  <p className="text-xs text-muted-foreground">Plays one video on a repeating interval (synced by clock across all stations)</p>
                </div>
              </label>
              {plForm.is_flagship && (
                <div className="flex items-center gap-2 pl-6">
                  <span className="text-sm text-muted-foreground">Play every</span>
                  <input type="number" min={1} max={60} value={plForm.flagship_interval_mins}
                    onChange={e => setPlForm(f => ({ ...f, flagship_interval_mins: Math.max(1, Math.min(60, parseInt(e.target.value) || 5)) }))}
                    className="w-16 h-8 px-2 rounded-md border border-input bg-background text-sm text-center" />
                  <span className="text-sm text-muted-foreground">minutes</span>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPlDialogOpen(false)}>Cancel</Button>
            <Button onClick={() => upsertPlaylist.mutate({ id: editingPlId ?? undefined, form: plForm })} disabled={!canSavePlaylist || upsertPlaylist.isPending}>
              {upsertPlaylist.isPending ? 'Saving…' : editingPlId ? 'Save Changes' : 'Create Playlist'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Assignment dialog ───────────────────────────────────────────────── */}
      <Dialog open={asgDialogOpen} onOpenChange={setAsgDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editingAsgId ? 'Edit Assignment' : 'New Assignment'}</DialogTitle></DialogHeader>
          <div className="space-y-5 py-2">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Playlist</Label>
              <select value={asgForm.playlist_id} onChange={e => setAsgForm(f => ({ ...f, playlist_id: e.target.value }))} className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm">
                <option value="">Select playlist…</option>
                {playlists.filter(p => !p.is_flagship).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>

            <div className="space-y-3">
              <Label className="text-xs text-muted-foreground">Targeting <span className="text-[10px] opacity-60">(leave all empty = all stations)</span></Label>
              <p className="text-[11px] text-muted-foreground bg-muted/50 rounded-md px-2.5 py-1.5 leading-relaxed">
                Within a row, selections are <strong>OR</strong> (any match).
                Across rows, selections are <strong>AND</strong> (must match all).
                <br />
                e.g. picking a station <em>and</em> a model means the station must also be that model.
              </p>

              {/* Live station count — without it, a targeting spec that matches
                  nothing (a station plus a model it isn't) looks identical to one
                  that works, and the station silently keeps the default playlist. */}
              {(() => {
                const matchCount  = getTargetedStationIds(asgForm.targeting, stations, builds).size;
                const totalActive = stations.filter(s => s.status !== 'retiring').length;
                const hasAny = !!(asgForm.targeting.locations?.length || asgForm.targeting.families?.length || asgForm.targeting.builds?.length || asgForm.targeting.stations?.length);
                return (
                  <p className={cn('text-xs font-medium flex items-center gap-1.5', matchCount === 0 && hasAny ? 'text-red-500' : 'text-muted-foreground')}>
                    <Monitor className="h-3 w-3" />
                    {matchCount} of {totalActive} station{totalActive !== 1 ? 's' : ''} matched
                    {matchCount === 0 && hasAny && ' — no stations will get this playlist'}
                  </p>
                );
              })()}

              {/* Locations */}
              <div className="space-y-1">
                <span className="text-[11px] font-medium text-muted-foreground flex items-center gap-1"><MapPin className="h-3 w-3" /> Shops</span>
                <div className="flex flex-wrap gap-1">
                  {locations.map(l => {
                    const sel = asgForm.targeting.locations?.includes(l.id);
                    return <button key={l.id} type="button" onClick={() => setAsgForm(f => {
                      const cur = f.targeting.locations ?? [];
                      const next = sel ? cur.filter(x => x !== l.id) : [...cur, l.id];
                      return { ...f, targeting: { ...f.targeting, locations: next } };
                    })} className={cn('px-2 py-0.5 rounded-full text-[11px] border transition-colors', sel ? 'bg-primary text-primary-foreground border-primary' : 'bg-muted/50 text-muted-foreground border-border hover:border-gray-400')}>{l.name}</button>;
                  })}
                </div>
              </div>

              {/* Families */}
              <div className="space-y-1">
                <span className="text-[11px] font-medium text-muted-foreground flex items-center gap-1"><Users className="h-3 w-3" /> Families</span>
                <div className="flex flex-wrap gap-1">
                  {Object.entries(PRODUCT_FAMILIES).map(([code, name]) => {
                    const sel = asgForm.targeting.families?.includes(code);
                    return <button key={code} type="button" onClick={() => setAsgForm(f => {
                      const cur = f.targeting.families ?? [];
                      const next = sel ? cur.filter(x => x !== code) : [...cur, code];
                      return { ...f, targeting: { ...f.targeting, families: next } };
                    })} className={cn('px-2 py-0.5 rounded-full text-[11px] border transition-colors', sel ? 'bg-primary text-primary-foreground border-primary' : 'bg-muted/50 text-muted-foreground border-border hover:border-gray-400')}>{name}</button>;
                  })}
                </div>
              </div>

              {/* Builds (model/SKU) */}
              <div className="space-y-1">
                <span className="text-[11px] font-medium text-muted-foreground flex items-center gap-1"><Package className="h-3 w-3" /> Model / SKU</span>
                <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                  {builds.filter(b => stations.some(s => s.build_id === b.id && s.status !== 'retiring')).map(b => {
                    const sel = asgForm.targeting.builds?.includes(b.id);
                    return <button key={b.id} type="button" onClick={() => setAsgForm(f => {
                      const cur = f.targeting.builds ?? [];
                      const next = sel ? cur.filter(x => x !== b.id) : [...cur, b.id];
                      return { ...f, targeting: { ...f.targeting, builds: next } };
                    })} className={cn('px-2 py-0.5 rounded-full text-[11px] border transition-colors', sel ? 'bg-primary text-primary-foreground border-primary' : 'bg-muted/50 text-muted-foreground border-border hover:border-gray-400')}>{b.sku}</button>;
                  })}
                </div>
              </div>

              {/* Stations */}
              <div className="space-y-1">
                <span className="text-[11px] font-medium text-muted-foreground flex items-center gap-1"><Monitor className="h-3 w-3" /> Stations</span>
                <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                  {stations.filter(s => s.status !== 'retiring').map(s => {
                    const sel = asgForm.targeting.stations?.includes(s.id);
                    return <button key={s.id} type="button" onClick={() => setAsgForm(f => {
                      const cur = f.targeting.stations ?? [];
                      const next = sel ? cur.filter(x => x !== s.id) : [...cur, s.id];
                      return { ...f, targeting: { ...f.targeting, stations: next } };
                    })} className={cn('px-2 py-0.5 rounded-full text-[11px] border transition-colors', sel ? 'bg-primary text-primary-foreground border-primary' : 'bg-muted/50 text-muted-foreground border-border hover:border-gray-400')}>{s.name}</button>;
                  })}
                </div>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="space-y-1 flex-1">
                <Label className="text-xs text-muted-foreground">Priority (higher wins)</Label>
                <Input type="number" min={0} max={100} value={asgForm.priority} onChange={e => setAsgForm(f => ({ ...f, priority: parseInt(e.target.value) || 0 }))} className="h-9 text-sm" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Active</Label>
                <div className="flex items-center h-9">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={asgForm.is_active} onChange={e => setAsgForm(f => ({ ...f, is_active: e.target.checked }))} className="h-4 w-4 rounded" />
                    {asgForm.is_active ? 'Live' : 'Paused'}
                  </label>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAsgDialogOpen(false)}>Cancel</Button>
            <Button onClick={() => upsertAssignment.mutate({ id: editingAsgId ?? undefined, form: asgForm })} disabled={!canSaveAssignment || upsertAssignment.isPending}>
              {upsertAssignment.isPending ? 'Saving…' : editingAsgId ? 'Save Changes' : 'Create Assignment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
